import {
  getBeaconOptions,
  getCamerasOptions,
  getServerOptions,
  postRoofForceRestartMutation,
  postRoofForceShutdownMutation,
  postRoofShutdownMutation,
  postServerForceRestartMutation,
  putBeaconMutation,
  putCamerasMutation,
  restartCamerasMutation,
} from '@/api/controlUnitApi'
import { QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Client } from '@/api/controlUnitApi/client/types.gen'
import { notifications } from '@mantine/notifications'
import { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { extendKey } from '@/utils/queryKey'

// Device restart timeouts (in milliseconds)
const RESTART_TIMEOUTS = {
  cameras: 150000, // 2.5 minutes
  server: 300000, // 5 minutes
  kit: 300000, // 5 minutes
} as const

export type RestartsDeviceType = keyof typeof RESTART_TIMEOUTS | 'beacon'

const useRestartsPanelData = ({ client, source }: { client: Client; source: string }) => {
  const { t } = useTranslation('hardware')
  const queryClient = useQueryClient()

  // Device mutex state - keeps track of restart operations
  const [deviceRestarting, setDeviceRestarting] = useState<Set<RestartsDeviceType>>(new Set())

  const timeoutRefs = useRef<Record<RestartsDeviceType, NodeJS.Timeout>>(
    {} as Record<RestartsDeviceType, NodeJS.Timeout>
  )

  // Helper to check if device can be operated on
  const isDeviceAvailable = useCallback(
    (deviceId: RestartsDeviceType) => {
      return !deviceRestarting.has(deviceId)
    },
    [deviceRestarting]
  )

  // Helper to mark device as restarting and set timeout
  const markDeviceRestartingWithTimeout = useCallback(
    (deviceId: RestartsDeviceType) => {
      setDeviceRestarting(prev => new Set(prev).add(deviceId))

      const timeout = RESTART_TIMEOUTS[deviceId as keyof typeof RESTART_TIMEOUTS]
      if (timeout) {
        timeoutRefs.current[deviceId] = setTimeout(() => {
          setDeviceRestarting(prev => {
            const next = new Set(prev)
            next.delete(deviceId)
            return next
          })

          notifications.show({
            title: t('deviceTimeout'),
            message: t('deviceRestartTimeout', { device: deviceId }),
            color: 'yellow',
            autoClose: 10000,
          })
        }, timeout)
      }
    },
    [t]
  )

  // Helper to mark device as available again
  const markDeviceAvailable = useCallback((deviceId: RestartsDeviceType) => {
    setDeviceRestarting(prev => {
      const next = new Set(prev)
      next.delete(deviceId)
      return next
    })

    // Clear any pending timeout
    if (timeoutRefs.current[deviceId]) {
      clearTimeout(timeoutRefs.current[deviceId])
      delete timeoutRefs.current[deviceId]
    }
  }, [])

  const camerasOpts = extendKey(getCamerasOptions({ client }), source)
  const beaconOpts = extendKey(getBeaconOptions({ client }), source)
  const serverOpts = extendKey(getServerOptions({ client }), source)
  const camerasQueryKey: QueryKey = camerasOpts.queryKey
  const beaconQueryKey: QueryKey = beaconOpts.queryKey
  const serverQueryKey: QueryKey = serverOpts.queryKey

  const { data: camerasState } = useQuery({ ...camerasOpts, retry: 1 })

  const { data: beaconState } = useQuery({ ...beaconOpts, retry: 1 })

  const { data: serverState } = useQuery({ ...serverOpts, retry: 1 })

  const { mutate: toggleCameras, isPending: isTogglingCameras } = useMutation({
    ...putCamerasMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('cameras')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('cameras') }),
          color: 'orange',
        })
        throw new Error('Cameras are busy')
      }
    },
    onSuccess: ({ enabled }) => {
      queryClient.invalidateQueries({ queryKey: camerasQueryKey })
      notifications.show({
        title: t('camerasStateChanged'),
        message: t('camerasStateMsg', { state: t(enabled ? 'enabled' : 'disabled') }),
        color: 'green',
      })
    },
    onError: error => {
      if (error.message !== 'Cameras are busy') {
        notifications.show({
          title: t('camerasToggleFailed'),
          message: t('failedToToggleCameras'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: restartCameras, isPending: isRestartingCameras } = useMutation({
    ...restartCamerasMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('cameras')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('cameras') }),
          color: 'orange',
        })
        throw new Error('Cameras are busy')
      }
      markDeviceRestartingWithTimeout('cameras')
      notifications.show({
        title: t('camerasRestartInitiated'),
        message: t('camerasRestartInitiatedMsg'),
        color: 'blue',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: camerasQueryKey })
      markDeviceAvailable('cameras')
      notifications.show({
        title: t('camerasRestartComplete'),
        message: t('camerasRestartedSuccessfully'),
        color: 'green',
      })
    },
    onError: error => {
      markDeviceAvailable('cameras')
      if (error.message !== 'Cameras are busy') {
        notifications.show({
          title: t('camerasRestartFailed'),
          message: t('failedToRestartCameras'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: toggleBeacon, isPending: isTogglingBeacon } = useMutation({
    ...putBeaconMutation({ client }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: beaconQueryKey })
      notifications.show({
        title: t('beaconStateChanged'),
        message: t('beaconStateChangedMsg'),
      })
    },
  })

  const { mutate: forceRestartServer, isPending: isForceRestartingServer } = useMutation({
    ...postServerForceRestartMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('server')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('server') }),
          color: 'orange',
        })
        throw new Error('Server is busy')
      }
      markDeviceRestartingWithTimeout('server')
      notifications.show({
        title: t('serverForceRestartInitiated'),
        message: t('serverForceRestartInitiatedMsg'),
        color: 'orange',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serverQueryKey })
      markDeviceAvailable('server')
      notifications.show({
        title: t('serverForceRestartComplete'),
        message: t('serverForceRestartCompleteMsg'),
        color: 'red',
      })
    },
    onError: error => {
      markDeviceAvailable('server')
      if (error.message !== 'Server is busy') {
        notifications.show({
          title: t('serverForceRestartFailed'),
          message: t('failedToForceRestartServer'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: shutdownKit, isPending: isShuttingDownKit } = useMutation({
    ...postRoofShutdownMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('kit')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyShuttingDown', { device: t('kit') }),
          color: 'orange',
        })
        throw new Error('Kit is busy')
      }
    },
    onSuccess: () => {
      notifications.show({
        title: t('kitShutdown'),
        message: t('kitShutdownMsg'),
      })
    },
    onError: error => {
      if (error.message !== 'Kit is busy') {
        notifications.show({
          title: t('kitShutdownFailed'),
          message: t('failedToShutdownKit'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: forceShotDownKit, isPending: isForceShuttingDownKit } = useMutation({
    ...postRoofForceShutdownMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('kit')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyShuttingDown', { device: t('kit') }),
          color: 'orange',
        })
        throw new Error('Kit is busy')
      }
    },
    onSuccess: () => {
      notifications.show({
        title: t('kitForceShutdown'),
        message: t('kitForceShutdownMsg'),
      })
    },
    onError: error => {
      if (error.message !== 'Kit is busy') {
        notifications.show({
          title: t('kitForceShutdownFailed'),
          message: t('failedToForceShutdownKit'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: forceRestartKit, isPending: isForceRestartingKit } = useMutation({
    ...postRoofForceRestartMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('kit')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('kit') }),
          color: 'orange',
        })
        throw new Error('Kit is busy')
      }
    },
    onSuccess: () => {
      notifications.show({
        title: t('kitForceRestart'),
        message: t('kitForceRestartMsg'),
      })
    },
    onError: error => {
      if (error.message !== 'Kit is busy') {
        notifications.show({
          title: t('kitForceRestartFailed'),
          message: t('failedToForceRestartKit'),
          color: 'red',
        })
      }
    },
  })

  return {
    camerasState,
    beaconState,
    serverState,

    isTogglingCameras,
    isRestartingCameras,
    isTogglingBeacon,
    isForceRestartingServer,
    isShuttingDownKit,
    isForceShuttingDownKit,
    isForceRestartingKit,

    deviceRestarting,
    isDeviceAvailable,

    forceRestartServer,
    toggleBeacon,
    restartCameras,
    toggleCameras,
    shutdownKit,
    forceShotDownKit,
    forceRestartKit,

    camerasQueryKey,
    beaconQueryKey,
    serverQueryKey,
  }
}

export default useRestartsPanelData
